import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { Button, Field } from '../components/sv';
import { AuthShell } from './LoginPage';

const COUNTRIES = [
  "China", "Japan", "South Korea", "Taiwan", "Hong Kong",
  "Singapore", "Malaysia", "Thailand", "Vietnam", "Indonesia", "Philippines",
  "India", "Bangladesh", "Pakistan", "Sri Lanka",
  "UAE", "Saudi Arabia", "Oman", "Qatar", "Bahrain", "Kuwait",
  "Germany", "Netherlands", "Belgium", "France", "United Kingdom", "Spain", "Italy", "Poland", "Greece", "Turkey", "Denmark", "Sweden", "Norway",
  "United States", "Canada", "Mexico",
  "Brazil", "Argentina", "Chile", "Colombia", "Peru",
  "South Africa", "Egypt", "Morocco", "Nigeria", "Kenya",
  "Australia", "New Zealand"
].sort();

function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);
  const [assignedAgent, setAssignedAgent] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', first_name: '', last_name: '', company: '', phone: '', country: '', city: '' });

  function handleChange(e) { setFormData(function (p) { return { ...p, [e.target.name]: e.target.value }; }); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name || !formData.country) { toast.error('Fill in all required fields'); return; }
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        email: formData.email, password: formData.password, first_name: formData.first_name, last_name: formData.last_name,
        company: formData.company || null, phone: formData.phone || null, country: formData.country, city: formData.city || null
      });
      setAssignedAgent(response.data.assigned_agent);
      setDone(true);
      toast.success('Registration submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  }

  if (done) {
    return (
      <AuthShell title="Registration received" text="Your account is pending approval. The agent for your region reviews new accounts within one business day." image="/img/container-stacks.jpg" testId="registration-pending">
        {assignedAgent && <div className="sv-card p-4 bg-gray-50 text-[15px]"><span className="text-gray-600">Reviewing agent</span><div className="font-medium mt-1">{assignedAgent}</div></div>}
        <p className="text-[15px] text-gray-700 mt-6">You will receive an email once the account is approved. You can then log in, request prices and book cargo.</p>
        <div className="grid grid-cols-2 gap-3 mt-8">
          <Button to="/login">Go to log in</Button>
          <Button variant="secondary" to="/">Back to home</Button>
        </div>
      </AuthShell>
    );
  }

  const input = "sv-input";
  return (
    <AuthShell title="Open an account" text="Book, pay and track online. Accounts are approved by your regional agent." image="/img/surveyor.jpg" testId="register-page">
      <form onSubmit={handleSubmit} className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="First name"><input name="first_name" value={formData.first_name} onChange={handleChange} className={input} data-testid="register-firstname" /></Field>
          <Field label="Last name"><input name="last_name" value={formData.last_name} onChange={handleChange} className={input} data-testid="register-lastname" /></Field>
        </div>
        <Field label="Work email"><input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="name@company.com" className={input} data-testid="register-email" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Country" hint="Determines your regional agent.">
            <select name="country" value={formData.country} onChange={handleChange} className={input} data-testid="register-country">
              <option value="">Select country</option>
              {COUNTRIES.map(function (c) { return <option key={c} value={c}>{c}</option>; })}
            </select>
          </Field>
          <Field label="City"><input name="city" value={formData.city} onChange={handleChange} className={input} data-testid="register-city" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Company"><input name="company" value={formData.company} onChange={handleChange} className={input} data-testid="register-company" /></Field>
          <Field label="Phone"><input name="phone" value={formData.phone} onChange={handleChange} className={input + " sv-mono"} data-testid="register-phone" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Password">
            <span className="relative block">
              <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} className={input + " pr-11"} data-testid="register-password" />
              <button type="button" onClick={function () { setShowPassword(!showPassword); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-ink" aria-label="Toggle password visibility">{showPassword ? <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} /> : <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />}</button>
            </span>
          </Field>
          <Field label="Confirm password"><input type={showPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className={input} data-testid="register-confirm-password" /></Field>
        </div>
        <Button type="submit" size="lg" disabled={loading} className="w-full mt-2" data-testid="register-submit">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Request account'}</Button>
        <p className="text-[15px] text-gray-700">Already registered? <Link to="/login" className="sv-link" data-testid="login-link">Log in</Link></p>
        <p className="text-[13px] text-gray-600">By requesting an account you accept the <Link to="/legal/terms" className="sv-link">terms and conditions</Link> and <Link to="/legal/privacy" className="sv-link">privacy notice</Link>.</p>
      </form>
    </AuthShell>
  );
}

export default RegisterPage;
